import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { View, Text, TextInput, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import { Dropdown } from 'react-native-element-dropdown';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { RootState } from '../../redux/store';
import { searchAddress, searchUspsAddress, retrieveUspsAddress, generateSessionToken } from '../../api/AllAddress ';
import { checkedInBarcode, getReservationDetail, scanBarcode, updateReservation, updateReservationItem } from '../../api/Reservation';
import { getColorcombinationsData, getStoreDetail } from '../../api/Settings';
import { getBrandDetail, getBrandsData } from '../../api/Price';
import { useAlertModal, useConfirmModal } from '../../common/hooks';
import BasicLayout from '../../common/components/CustomLayout/BasicLayout';
import { BOHTBody, BOHTD, BOHTDInput, BOHTH, BOHTHead, BOHTR, BOHTable } from '../../common/components/bohtable';
import { BOHButton, BOHToolbar } from '../../common/components/bohtoolbar';
import { CommonSelectDropdown } from '../../common/components/CommonSelectDropdown/CommonSelectDropdown';
import LabeledTextInput from '../../common/components/bohform/LabeledTextInput';
import { msgStr } from '../../common/constants/Message';
import { printReservation } from '../../common/utils/Print';

import { actionOrderStyle } from './styles/ActionOrderStyle';
import { formatDate, formatDate2 } from '../../common/utils/DateUtils';
import { getAddressLabel } from '../../common/utils/Helpers';

interface Props {
  openOrderScreen: (itemName: string, data?: any ) => void;
  initialData?: any;
}

export const ActionOrder = ({ openOrderScreen, initialData }: Props) => {

  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [orderInfo, setOrderInfo] = useState<any>({});
  const [colors, setColors] = useState([{id:null, color_key:'Off color', combination:'', color:' '}]);
  const [brands, setBrands] = useState<Array<any>>([]);
  const [brandDetail, setBrandDetail] = useState<any>({});

  const profileSettings = useSelector((state: RootState) => state.profileSettings.settings);
  const allowBrandChange = useMemo(() => {
    const setting = profileSettings?.find((s) => s.item_key === 'allow_reservation_brand_change');
    return `${setting?.item_value}`.toLowerCase() === 'true';
  }, [profileSettings]);
  const [barcode, SetBarcode] = useState(initialData.barcode || '');
  const [nextDisable, setNextDisable] = useState(true);

  const [searchedAddresses, setSearchedAddresses] = useState<Array<any>>([]);
  const [selectedAddress, selectAddress] = useState(null);
  const [searchKey, setSearchKey] = useState('');
  const [requireUsps, setRequireUsps] = useState(false);
  const [uspsZipCodes, setUspsZipCodes] = useState<string>('');
  const uspsSessionToken = useRef(generateSessionToken());

  const [inputValues, setInputValues] = useState({
    email: '',
    phone_number: '',
    color_id: null,
    note: '',
    deliveryAddress: '',
  });
  const updateTimer = useRef(null);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }

    const fetchData = async () => {
      await getReservationDetail(initialData.orderId, (jsonRes, status)=>{
        if(status == 200){
          setOrderInfo(jsonRes);
          if(initialData.barcode && jsonRes) scanBarcodeHandle(initialData.barcode, jsonRes);
        }else setOrderInfo({});
      });
      await getColorcombinationsData((jsonRes)=>{
        setColors([{id:null, color_key:'Off color', combination:'', color:' '}, ...jsonRes]);
      });
    }
  
    fetchData();
  }, []);
  
  useEffect(()=>{
    if(orderInfo){
      let disable = false
      if(orderInfo.stage == 4) disable = true;
      else if(orderInfo.items){
        if(orderInfo.stage == 2){
          orderInfo.items.map((item)=>{
            if(item.status != 3) disable=true;
          });
        }else if(orderInfo.stage == 3){
          orderInfo.items.map((item)=>{
            if(item.status != 4) disable=true;
          });
        }else disable=true;
      }
      setNextDisable(disable)
      const deliveryAddress = orderInfo.use_manual? orderInfo.manual_address: (orderInfo?.all_addresses?.number??'') + ' ' + (orderInfo?.all_addresses?.street??'') + ' ' + (orderInfo?.all_addresses?.plantation??'') + ' ' + (orderInfo?.all_addresses?.property_name??'');
      setInputValues({
        email: orderInfo?.email || orderInfo?.customer?.email || '',
        phone_number: orderInfo?.phone_number || orderInfo?.customer?.phone_number || '',
        color_id: orderInfo?.color?.id??null,
        note: orderInfo?.note || '',
        deliveryAddress: deliveryAddress,
      })

      if(orderInfo.use_manual) setSearchKey(orderInfo?.manual_address??'');
      else if(orderInfo.all_addresses) setSearchKey(`${orderInfo.all_addresses.number || ''} ${orderInfo.all_addresses.street || ''} ${orderInfo.all_addresses.property_name? `${orderInfo.all_addresses.property_name}` :''}, ${orderInfo.all_addresses.plantation || ''}`);
    }else {
      setInputValues({
        email: '',
        phone_number: '',
        color_id: null,
        note: '',
        deliveryAddress: '',
      })
    }
  }, [orderInfo])

  useEffect(()=>{
    if(allowBrandChange){
      getBrandsData((jsonRes, status)=>{
        if(status == 200 && Array.isArray(jsonRes)) setBrands(jsonRes);
        else setBrands([]);
      });
    }
  }, [allowBrandChange])

  useEffect(()=>{
    if(orderInfo && orderInfo.brand_id){
      getBrandDetail({id:orderInfo.brand_id}, (jsonRes, status)=>{
        if(status == 200) setBrandDetail(jsonRes);
        else setBrandDetail({});
      });
    } else setBrandDetail({});
  }, [(orderInfo && orderInfo.brand_id)])

  useEffect(() => {
    const bId = orderInfo?.brand_id;
    if (!bId) { setRequireUsps(false); return; }
    getStoreDetail(bId, (jsonRes:any, status:number) => {
      setRequireUsps(status === 200 && !!jsonRes?.require_usps_address);
      setUspsZipCodes(jsonRes?.usps_zip_codes || '');
    });
  }, [(orderInfo && orderInfo.brand_id)])

  const brandsDropdownData = useMemo(() => {
    if (!brands?.length) return [];
    return brands.map((item, index) => ({
      value: item,
      displayLabel: item.brand,
      index,
    }));
  }, [brands]);

  const defaultBrand = useMemo(() => {
    if (orderInfo && orderInfo.brand_id)
      return brandsDropdownData.find((item) => item.value.id == orderInfo.brand_id) || null;
    return null;
  }, [orderInfo, brandsDropdownData]);

  const handleBrandChange = (brandId) => {
    const payload: any = {
      id: orderInfo.id,
      brand_id: brandId,
    };

    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          setOrderInfo(prev => ({ ...prev, brand_id: brandId }));
          getBrandDetail({ id: brandId }, (bRes, bStatus) => {
            if (bStatus == 200) setBrandDetail(bRes);
          });
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const updateItem = (payload, index) => {
    updateReservationItem(payload, (jsonRes, status)=>{
      if(status == 200){
        setOrderInfo(prevOrderInfo => {
          const updatedItems = [...prevOrderInfo.items];
          updatedItems[index] = {
            ...updatedItems[index],
            status: payload.status,
            barcode: payload.barcode
          };
          return { ...prevOrderInfo, items: updatedItems };
        });
      }
    });
  }

  const renderTableData = () => {
    const rows = [];
    if (orderInfo.items && orderInfo.items.length > 0) {
      orderInfo.items.map((item, index) => {
        let statusStr = item.status == 3?'Checked out' : item.status == 4 ? 'Checked in' : '';
        rows.push(
          <BOHTR key={index}>
            <BOHTD style={{flex:1}}>{item.display_name}</BOHTD>
            <BOHTDInput 
              width={100} 
              value={item.barcode || ''}
              editable={(item.status == 2 || item.status == 3 )? true: false}
              onChangeText={(value)=>{
                const payload = {
                  id: item.id,
                  status: item.status >= 3 ? 4 : 3,
                  barcode: value,
                }
                console.log(payload)
                console.log(item.status)
                updateItem(payload, index);
              }}
            />
            <BOHTD width={100}>{statusStr}</BOHTD>
          </BOHTR>
        );
      });
    } else {
      <></>;
    }
    return <>{rows}</>;
  };

  const scanBarcodeHandle = (barcode, orderInfo) => {
    const barCode = barcode.trim();
    if(!barCode) {
      showAlert('warning', 'There is no scanned barcode');
      return;
    }

    const payload = {
      barcode: barCode,
      reservation_id: orderInfo.id,
    }
    
    if(orderInfo.stage == 2){
      scanBarcode(payload, (jsonRes, status)=>{
        switch (status) {
          case 200:
            const updatedId = jsonRes.item_id;
            const barcode = jsonRes.barcode;
            setOrderInfo(prev=>{
              return {
                ...prev,
                items: prev.items.map(item => {
                  if (item.id === updatedId) {
                    return {
                      ...item,
                      barcode: barcode,
                      status: 3
                    };
                  } else {
                    return item;
                  }
                })
              };
            })
            SetBarcode('');
          barcodeInputRef.current.focus();
          break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    }else if(orderInfo.stage == 3){
      checkedInBarcode(payload, (jsonRes, status)=>{
        switch (status) {
          case 200:
            const updatedId = jsonRes.item_id;
            const barcode = jsonRes.barcode;
            setOrderInfo(prev=>{
              return {
                ...prev,
                items: prev.items.map(item => {
                  if (item.id === updatedId) {
                    return {
                      ...item,
                      barcode: barcode,
                      status: 4
                    };
                  } else {
                    return item;
                  }
                })
              };
            })
            SetBarcode('');
            barcodeInputRef.current.focus();
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    }
  }

  const handleInputChange = (fieldName, value) => {
    const newValues:any = {
      ...inputValues,
      [fieldName]: value
    };
    if(fieldName === 'color_id'){
      const newColor = colors.find(item => item.id == value);
      newValues.color = newColor; 
    }
    setInputValues(newValues);

    const payload = {
      id: orderInfo.id,
      [fieldName]:value || null
    }
    if (updateTimer.current) {
      clearTimeout(updateTimer.current);
    }

    updateTimer.current = setTimeout(() => {
      updateReservation(payload, (jsonRes, status) => {
        switch (status) {
          case 201:
            break;
        }
      });
    }, 1000);
  }

  const getAddresses = useCallback(async (q:any) => {
    setSearchKey(q);
    selectAddress(null);
    const filterToken = q.toLowerCase();

    // USPS Address Lock-in: live verified-address typeahead when required.
    if (requireUsps) {
      try {
        const uspsRes = await searchUspsAddress(filterToken, undefined, uspsZipCodes || undefined, uspsSessionToken.current);
        const suggestions = await uspsRes.json();
        setSearchedAddresses(Array.isArray(suggestions)
          ? suggestions.map((a:any) => ({
              label: a.full_address,
              full_address: a.full_address,
              mapbox_id: a.mapbox_id,
              latitude: a.latitude,
              longitude: a.longitude,
              __usps: true,
            }))
          : []);
      } catch {
        setSearchedAddresses([]);
      }
      return;
    }

    const response = await searchAddress(filterToken, orderInfo.brand_id);
    const items = await response.json();
    if (Array.isArray(items)) {
      setSearchedAddresses(
        items.map((address, index) => {
          let addressOption = {
            ...address,
            label: getAddressLabel(address, index) 
          }
          return addressOption;
        })
      );
    } else {
      setSearchedAddresses([]);
    }
  }, [orderInfo, requireUsps])

  const handleAddressChange = async (event, value) => {
    // USPS Address Lock-in: a verified pick has no all_addresses row — persist
    // it as a manual (standardized) address plus the verified cross-reference.
    // Suggestions from the Search Box API don't include lat/lng, so resolve
    // them via retrieveUspsAddress before persisting.
    if (value?.__usps || value?.mapbox_id) {
      let full_address = value.full_address;
      let latitude = value.latitude;
      let longitude = value.longitude;

      if (latitude == null || longitude == null) {
        try {
          const retrieveRes = await retrieveUspsAddress(value.mapbox_id, undefined, uspsSessionToken.current);
          const resolved = await retrieveRes.json();
          if (resolved && resolved.mapbox_id) {
            full_address = resolved.full_address || full_address;
            latitude = resolved.latitude;
            longitude = resolved.longitude;
          }
        } catch {
          // fall through with whatever we have; updateReservation still
          // persists the verified mapbox_id/address even without GPS.
        }
        // Start a fresh session for the next search (billing groups suggest+retrieve).
        uspsSessionToken.current = generateSessionToken();
      }

      const uspsPayload: any = {
        id: orderInfo.id,
        use_manual: true,
        manual_address: full_address,
        mapbox_id: value.mapbox_id,
        usps_address: full_address,
        lat: latitude,
        lng: longitude,
        manually_updated: true,
        is_address_synced: false,
      };
      updateReservation(uspsPayload, (jsonRes, status) => {
        switch (status) {
          case 201:
            selectAddress({ ...value, full_address, latitude, longitude });
            setSearchKey(full_address);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
      return;
    }

    const payload={
      id: orderInfo.id,
      address_id : value.id,
      use_manual : false,
    }
    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          selectAddress(value);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    })
  }

  const processNextStage = () => {
    const payload = {
      id: orderInfo.id,
      stage: (orderInfo.stage * 1 + 1),
    }
    
    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          setOrderInfo(prev => {
            return { ...prev, stage: (prev.stage + 1) };
          });
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    })
  }

  const printPDF = () => {
    showConfirm(
      'Do you want to include Terms and Conditions?', 
      () => {
        printReservation(orderInfo.id, 1, true);
      },
      () => {
        printReservation(orderInfo.id, 1, false);
      },
      'No'
    );
  }
  return (
    <BasicLayout
      goBack={()=>{
        openOrderScreen('Orders List');
      }} 
      screenName={'Proceed Order'} 
    >
      <div style={{overflow:'auto', padding:'0 30px'}}>
        <div style={{width:'fit-content', margin:'auto'}}>
        <View style={styles.container}>
          <BOHToolbar style={{justifyContent:'flex-end', alignItems:'center',}}>
            <BOHButton
              label='Print'
              onPress={printPDF}
            />
            {/* <BOHButton
              style={{marginLeft:20}}
              label='Update'
              onPress={()=>UpdateDetails()}
            /> */}
          </BOHToolbar>
          <View style={{flexDirection:'row', marginVertical:4}}>
            <LabeledTextInput
              label='Start date'
              width={300}
              containerStyle={{marginRight:30}}
              placeholder='Start date'
              placeholderTextColor="#ccc"
              value={orderInfo.start_date ? formatDate2(new Date(`${orderInfo.start_date} 00:00:00`)) : ''}
              // onChangeText={value => handleInputChange('billableDays', value)}
              editable={false}
            />
            <LabeledTextInput
              label='End date'
              width={300}
              placeholder='End date'
              placeholderTextColor="#ccc"
              value={orderInfo.end_date ? formatDate2(new Date(`${orderInfo.end_date} 00:00:00`)) : ''}
              // onChangeText={value => handleInputChange('billableDays', value)}
              editable={false}
            />
          </View>
          {/* <View style={{flexDirection:'row', marginVertical:4}}>
            <LabeledTextInput
              label='Start location'
              width={300}
              containerStyle={{marginRight:30}}
              placeholder='Start location'
              placeholderTextColor="#ccc"
              value={orderInfo.start_location}
              // onChangeText={value => handleInputChange('billableDays', value)}
              editable={false}
            />
            <LabeledTextInput
              label='End location'
              width={300}
              placeholder='End location'
              placeholderTextColor="#ccc"
              value={orderInfo.end_location}
              // onChangeText={value => handleInputChange('billableDays', value)}
              editable={false}
            />
          </View> */}
          <View style={{flexDirection:'row', marginVertical:4}}>
            <LabeledTextInput
              label='Order Number'
              width={300}
              containerStyle={{marginRight:30}}
              placeholder='Order Number'
              placeholderTextColor="#ccc"
              value={orderInfo.order_number}
              editable={false}
            />
            <LabeledTextInput
              label='Customer Name'
              width={300}
              placeholder='Customer Name'
              placeholderTextColor="#ccc"
              value={orderInfo.customer? orderInfo.customer?.first_name + ' ' + orderInfo.customer?.last_name : ''}
              editable={false}
            />
          </View>
          <View style={{flexDirection:'row', marginVertical:4, zIndex:20}}>
            {allowBrandChange ? (
              <View style={{width:300, marginRight:30}}>
                <CommonSelectDropdown
                  width={300}
                  height={40}
                  onItemSelected={(item) => {
                    handleBrandChange(item.value.id);
                  }}
                  data={brandsDropdownData}
                  placeholder="Select A Brand"
                  title={'Brand'}
                  titleStyle={{marginBottom:6, color:"#555555", fontSize:14}}
                  defaultValue={defaultBrand}
                />
              </View>
            ) : (
              <LabeledTextInput
                label='Brand'
                width={300}
                containerStyle={{marginRight:30}}
                placeholder='Brand'
                placeholderTextColor="#ccc"
                value={brandDetail?.brand ?? ''}
                editable={false}
              />
            )}
          </View>
          <View style={{flexDirection:'row', marginVertical:4}}>
            <LabeledTextInput
              containerStyle={{marginRight:30}}
              label='Email'
              width={300}
              placeholder='Email'
              placeholderTextColor="#ccc"
              value={inputValues.email}
              onChangeText={value => handleInputChange('email', value)}
            />
            {/* <LabeledTextInput
              label='Mobile phone'
              width={300}
              containerStyle={{marginRight:30}}
              placeholder='Mobile phone'
              placeholderTextColor="#ccc"
              value={orderInfo.phone_number || orderInfo?.customer?.mobile_phone || ''}
              onChangeText={value => handleInputChange('billableDays', value)}
              editable={false}
            /> */}
            <LabeledTextInput
              label='Phone Number'
              width={300}
              placeholder='Phone Number'
              placeholderTextColor="#ccc"
              value={inputValues.phone_number}
              onChangeText={value => handleInputChange('phone_number', value)}
            />
          </View>
          <View style={{flexDirection:'row', marginVertical:4}}>
            <View style={{width:300, marginRight:30}}>
              <Text style={styles.label}>Color</Text>
                <Dropdown
                  style={[styles.select, { backgroundColor: colors.find(item=>(item.id == inputValues.color_id))?.color || ' ' }]}
                  placeholderStyle={{color:'#ccc'}}
                  data={colors}
                  maxHeight={300}
                  labelField="color_key"
                  valueField="id"
                  placeholder="Select color"
                  value={inputValues.color_id}
                  renderItem={item=>(
                    <View style={{backgroundColor:item.color, flexDirection:'row', justifyContent:'space-between', paddingVertical:8, paddingHorizontal:12}}>
                      <Text>{item.color_key}</Text>
                      <Text>{item.color}</Text>
                    </View>
                  )}
                  onChange={item => {
                    handleInputChange('color_id', item.id);
                  }}
                />
            </View>
            <LabeledTextInput
              label='Combination'
              width={300}
              placeholder='Combination'
              placeholderTextColor="#ccc"
              value={colors.find(item=>(item.id == inputValues.color_id))?.combination || ' ' }
              onChangeText={value => handleInputChange('billableDays', value)}
              editable={false}
            />
          </View>
          <View style={[styles.orderRow, {width:'100%'}]}>
            {Platform.OS === 'web' ?
              <>
                <Text style={{marginBottom:6, color:"#555555"}}>{'Delivery Address'}</Text>
                <Autocomplete
                  // Multiple={true}
                  freeSolo
                  sx={{ 
                    width: '100%', 
                    padding: '0px',
                    borderRadius: '0px',
                  }}
                  disableClearable
                  options={searchedAddresses}
                  value={selectedAddress}
                  inputValue={searchKey}
                  onChange={handleAddressChange}
                  onInputChange={(event, value)=>{
                    getAddresses(value);
                  }}
                  onClose = {(event: React.SyntheticEvent, reason: string)=>{
                    
                  }}
                  filterOptions={(x) => {
                    return x;
                  }}
                  renderInput={(params) => {
                    const text = String(params?.inputProps?.value ?? ' ');
                    const spanIndex = text.indexOf('<span');
                    const inputVal = spanIndex !== -1 ? text.substring(0, spanIndex) : text;
                    return (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          type: 'search',
                          value: inputVal,
                          style: {
                            padding:'2px',
                            fontSize:'14px',
                          }
                        }}
                      />
                    )
                  }}
                  renderOption={(props, option) => {
                    const htmlLabel = { __html: option.label };
                    return (
                      <li {...props}>
                        <span style={{fontSize:'14px'}} dangerouslySetInnerHTML={htmlLabel} />
                      </li>
                    );
                  }}
                />
              </>
              :
              <LabeledTextInput
                label='Delivery Address'
                width={"100%"}
                placeholder='Delivery Address'
                placeholderTextColor="#ccc"
                inputStyle={{marginVertical:6}}
                editable={false}
                value={inputValues.deliveryAddress}
                onChangeText={value => handleInputChange('group', value)}
              />}
          </View>
          <View style={{flexDirection:'row', marginVertical:4}}>
            <LabeledTextInput
              label='Notes'
              containerStyle={{marginTop:8}}
              width={630}
              placeholder='Notes'
              placeholderTextColor="#ccc"
              multiline={true}
              inputStyle={{height:100}}
              value={inputValues.note}
              onChangeText={value => handleInputChange('note', value)}
            />
          </View>
          {/* <View style={{flexDirection:'row'}}>
            <TouchableOpacity >
              <Text style={styles.buttonText}>CANCEL</Text>
            </TouchableOpacity>
            <TouchableOpacity >
              <Text style={styles.buttonText}>NEXT</Text>
            </TouchableOpacity>
          </View> */}
          <View style={[styles.orderRow, {justifyContent:'flex-end'}]}>
          </View>
          <BOHToolbar style={{justifyContent:'space-between', alignItems:'center',}}>
            <BOHButton 
              disabled={nextDisable}
              label={orderInfo.stage == 2 && "Check Out " || orderInfo.stage == 3 && "Check In" || 'Completed'}
              onPress={processNextStage}
            />
            <Text style={{fontWeight:'bold', fontSize:16}}>{orderInfo.stage == 3 && "All Checked Out!" || orderInfo.stage == 4 && "All Checked In!"}</Text>
            <View style={{flexDirection:'row'}}>
              <TextInput 
                ref={barcodeInputRef}
                editable={orderInfo.stage == 2 || orderInfo.stage == 3 ? true : false}
                style={{ 
                  height: 40,
                  borderColor: 'gray',
                  borderWidth: 1,
                  padding: 8,
                  paddingVertical: 4,
                  borderRadius: 4,
                  marginRight: 20,
                }}
                value={barcode}
                onChangeText={SetBarcode}
                onSubmitEditing={()=>scanBarcodeHandle(barcode, orderInfo)} 
              />
              <BOHButton 
                style={{alignSelf:'center'}}
                disabled={(orderInfo.stage == 2 || orderInfo.stage == 3) && nextDisable ? false : true}
                label='Scan'
                onPress={()=>scanBarcodeHandle(barcode, orderInfo)}
              />
            </View>
          </BOHToolbar>
          <BOHTable>
            <BOHTHead>
              <BOHTR>
                <BOHTH style={{flex:1}}>{'item'}</BOHTH>
                <BOHTH width={100}>{'Barcode'}</BOHTH>
                <BOHTH width={100}>{'status'}</BOHTH>
              </BOHTR>
            </BOHTHead>
            <BOHTBody>
              {renderTableData()}
            </BOHTBody>
          </BOHTable>
        </View>
        </div>
      </div>
    </BasicLayout>
  );
};

const styles = actionOrderStyle;
